import { faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { BlackbaudRaisersEdgeIntegration } from '@feathr/blackbox';
import type { IIntegrationUIOption, IIntegrationUISelectSettingField } from '@feathr/components';
import { Tooltip, UniversalSelect } from '@feathr/components';

import { useFundOptions } from './RaisersEdgeFundSelect.hooks';

import * as styles from './RaisersEdgeFundSelect.css';

interface IProps {
  integration: BlackbaudRaisersEdgeIntegration;
  /** Loads the funds the first time this is true, i.e. once donation upsync is on. */
  enabled: boolean;
  /** The fund field from the config: its label and placeholder. */
  field: IIntegrationUISelectSettingField;
  /** Disables the select with this reason, e.g. while the page saves. */
  disabledReason?: string;
}

function RaisersEdgeFundSelect({
  disabledReason,
  enabled,
  field,
  integration,
}: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const options = useFundOptions(integration, enabled);
  const fundId = integration.get('fundraising')?.fund_id;

  function noOptionsMessage(): string {
    return t('No funds found');
  }

  // Stage the edit on the model; the page's Save & Sync persists it.
  function handleSelect(option: IIntegrationUIOption): void {
    integration.set({ fundraising: { ...integration.get('fundraising'), fund_id: option.id } });
  }

  return (
    <Tooltip className={styles.tooltip} title={disabledReason}>
      <UniversalSelect<IIntegrationUIOption>
        controlPrefix={<FontAwesomeIcon icon={faMagnifyingGlass} />}
        disabled={Boolean(disabledReason)}
        isLoading={options === undefined}
        isMulti={false}
        label={field.label}
        name={'raisers-edge-fund'}
        noOptionsMessage={noOptionsMessage}
        onSelectSingle={handleSelect}
        options={options ?? []}
        placeholder={field.placeholder}
        value={options?.find((option) => option.id === fundId) ?? null}
      />
    </Tooltip>
  );
}

export default observer(RaisersEdgeFundSelect);
